/**
 * useChatHomeSessions — the chat home's data spine, shared by the desktop
 * ChatHomeView and the mobile MobileChatScreen so the two surfaces can't
 * drift: recent projects, per-project session lists (grouped by project,
 * groups ranked by their newest conversation), the tenant-wide busy scan
 * driving the running dots, the finished-while-away notification dots, the
 * controlled active project/session pair, and rename/delete actions.
 *
 * Sessions are workspace(=project)-scoped, but the rail's previews come from
 * ONE batched call (GET /api/agent/sessions/recent — pass the rail's ids, get
 * back the non-empty groups); the running indicator comes from ONE tenant-wide
 * busy scan (GET /api/agent/sessions/status) polled while a consumer is mounted.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { request, getActiveTenantId, getActiveOrgEmail } from '@papan/api-client'
import { useConfirm } from '@papan/core-ui/ui'
import { isMiniappFolder } from '@papan/types'
import { useProjects, useRecentProjects, type ProjectItem } from '@papan/files/ui'
import { useBusySessions } from '@papan/agent/ui'

/**
 * The ambient "Papan AI" assistant, lazily provisioned as a system
 * workspace under the user's Archangel home. It's the DEFAULT hero target on
 * the chat home — available even with zero projects (and for viewers). Shaped
 * as a ProjectItem so it drops into the same HeroChat wiring as a project.
 * See docs/plans/2026-07-12_WORKSPACE_ASSISTANT_SCOPED_SANDBOX.md §3.3.
 */
export function useDriveAssistantWorkspace() {
  return useQuery<ProjectItem>({
    queryKey: ['system-workspace', 'drive', getActiveTenantId(), getActiveOrgEmail()],
    queryFn: async () => {
      const r = await request<{ workspace: { id: string; name: string }; folderId: string }>(
        '/api/workspaces/ensure-system',
        { method: 'POST', body: JSON.stringify({ surface: 'drive' }) },
      )
      return { id: r.workspace.id, folderId: r.folderId, name: r.workspace.name, path: '' }
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

/** Serialized Session.Info rows from GET /api/agent/sessions (subset we use). */
export interface ChatSessionRow {
  id: string
  title: string
  model: string
  time: { created: number; updated: number }
}

const RAIL_PROJECT_CAP = 8
const RAIL_SESSIONS_PER_PROJECT = 6

export interface ChatSessionGroup {
  project: ProjectItem
  sessions: ChatSessionRow[]
  newest: number
  /** The ambient Papan AI assistant group — rendered with a sparkle icon
   *  and pinned to the top of the rail, not a user project. */
  isAssistant?: boolean
}

export function useChatHomeSessions() {
  const queryClient = useQueryClient()
  // ⚠️ `data` CANNOT CARRY THE WAIT, for either of these, and three rounds
  // of review went into learning it once:
  //
  //   * `allProjects` is `undefined` while in flight, `[]` when the member
  //     genuinely has none, AND `undefined` again when the query ERRORED
  //     (no placeholder data, so a failed fetch leaves it unset);
  //   * `driveAssistant` is `null` both while asking and when there is none.
  //
  // A caller that must WAIT for one of these — the chat deep link — cannot
  // read any of that off `data`. Round 2 gave the assistant a pending flag
  // and left the project list reading `undefined`, which turned "settles
  // too early" into "never settles at all" on an errored list (round 3).
  // So BOTH report the same way, and the deep link waits on one shape.
  const { data: allProjects, isPending: projectsPending } = useProjects()
  const { data: recentProjects } = useRecentProjects()
  const { data: driveAssistant, isPending: driveAssistantPending } = useDriveAssistantWorkspace()

  // Every project, recently opened first, backfilled from the full list by
  // modified/created date — same merge the drive home uses. Shared source of
  // truth so both the rail and the Apps grid order by real recency (opening or
  // creating an app bumps it here) instead of the server's raw list order.
  const orderedProjects = useMemo<ProjectItem[]>(() => {
    const seen = new Set<string>()
    const out: ProjectItem[] = []
    const byModified = [...(allProjects ?? [])].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    for (const p of [...(recentProjects ?? []), ...byModified]) {
      if (seen.has(p.id)) continue
      seen.add(p.id)
      out.push(p)
    }
    return out
  }, [allProjects, recentProjects])

  // Projects whose sessions feed the list (capped).
  const railProjects = useMemo<ProjectItem[]>(
    () => orderedProjects.slice(0, RAIL_PROJECT_CAP),
    [orderedProjects],
  )

  const miniapps = useMemo(
    () => orderedProjects.filter(p => isMiniappFolder(p.name)).slice(0, 8),
    [orderedProjects],
  )
  // Top 5 by recency, apps included — an app entry renders with a pink box
  // icon in the panel (see ChatShortcuts). Drawn from the uncapped ordering
  // so it isn't starved by railProjects' top-8 cut.
  const shortcutProjects = useMemo(
    () => orderedProjects.slice(0, 5),
    [orderedProjects],
  )

  // ── Controlled active project/session pair ──────────────────────────
  // Default hero target = the ambient Papan AI assistant (always present,
  // even with zero projects); an explicit rail selection overrides it. Falls
  // back to railProjects[0] only while the assistant is still provisioning.
  const [selected, setSelected] = useState<{ project: ProjectItem; session: { id: string | null; nonce: number } | null } | null>(null)
  const activeProject = selected?.project ?? driveAssistant ?? railProjects[0] ?? null
  const activeSessionId = selected?.session?.id ?? null

  // ── Sessions per surface, grouped + recency-ranked ──────────────────
  // The Papan AI assistant fans out alongside projects (first, so its
  // group index is stable) — its sessions belong in the rail too.
  const railEntries = useMemo<ProjectItem[]>(() => (
    driveAssistant ? [driveAssistant, ...railProjects.filter(p => p.id !== driveAssistant.id)] : railProjects
  ), [driveAssistant, railProjects])
  // ONE batched call for the whole rail (was one GET /sessions per project —
  // which 404'd for every never-opened project, spamming the console every 60s).
  // The server returns only the non-empty groups; empties/unopened just don't
  // appear. Keyed by the rail's id-set, so adding/removing/reordering a project
  // mints a new key; keepPreviousData holds the last groups through that refetch
  // so the rail doesn't blank out on every recency reshuffle (the per-project
  // useQueries kept each project cached independently — this restores that feel).
  const railKey = railEntries.map(p => p.id).join(',')
  const recentQuery = useQuery({
    queryKey: ['agent', 'home-recent-sessions', railKey],
    queryFn: () => railKey
      ? request<{ workspaceId: string; sessions: ChatSessionRow[] }[]>(
          `/api/agent/sessions/recent?workspaceIds=${encodeURIComponent(railKey)}&limit=${RAIL_SESSIONS_PER_PROJECT}`,
        )
      : Promise.resolve([]),
    enabled: railEntries.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 20_000,
    refetchInterval: 60_000,
  })
  // Grouped by surface: the Papan AI assistant is pinned to the top; the
  // rest rank by recency (freshest conversation floats up). Sessions inside a
  // group are newest-first. Surfaces with no conversations don't show.
  const sessionGroups = useMemo<ChatSessionGroup[]>(() => {
    const byWs = new Map((recentQuery.data ?? []).map(g => [g.workspaceId, g.sessions]))
    const groups: ChatSessionGroup[] = []
    railEntries.forEach((project) => {
      const rows = (byWs.get(project.id) ?? []).slice().sort((a, b) => b.time.updated - a.time.updated)
      if (rows.length) groups.push({ project, sessions: rows, newest: rows[0].time.updated, isAssistant: project.id === driveAssistant?.id })
    })
    groups.sort((a, b) => (a.isAssistant === b.isAssistant ? b.newest - a.newest : a.isAssistant ? -1 : 1))
    return groups
  }, [railEntries, driveAssistant, recentQuery.data])

  // ── Tenant-wide busy scan → running dots ────────────────────────────
  // Shared with useAgentSession / useSessionActivity — same key, one poll.
  const busyQuery = useBusySessions()
  const busyIds = useMemo(() => new Set((busyQuery.data ?? []).map(b => b.id)), [busyQuery.data])

  // Done-notification dots: a session that LEAVES the busy set while it
  // isn't the focused one keeps a dot until clicked.
  const prevBusyRef = useRef<Set<string> | null>(null)
  const activeSessionIdRef = useRef(activeSessionId)
  activeSessionIdRef.current = activeSessionId
  const [doneIds, setDoneIds] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    if (!busyQuery.data) return
    const prev = prevBusyRef.current
    prevBusyRef.current = busyIds
    if (!prev) return
    const finished = [...prev].filter(id => !busyIds.has(id) && id !== activeSessionIdRef.current)
    if (!finished.length) return
    setDoneIds(s => new Set([...s, ...finished]))
    // Titles/ordering may have changed while the run was in flight.
    void queryClient.invalidateQueries({ queryKey: ['agent', 'home-recent-sessions'] })
  }, [busyIds, busyQuery.data, queryClient])

  const openSession = useCallback(async (project: ProjectItem, sessionId: string | null) => {
    let sid = sessionId
    // New conversation (null): eagerly create an untitled session NOW rather
    // than lazily on first message/upload — the lazy path was bug-prone. Falls
    // back to the null (lazy) path only if the create request fails.
    if (sid === null) {
      try {
        const data = await request<{ id: string }>('/api/agent/sessions', {
          method: 'POST',
          body: JSON.stringify({ workspaceId: project.id }),
        })
        sid = data.id
        void queryClient.invalidateQueries({ queryKey: ['agent', 'home-recent-sessions'] })
      } catch { sid = null }
    }
    setSelected({ project, session: { id: sid, nonce: Date.now() } })
    if (sid) setDoneIds(s => { if (!s.has(sid!)) return s; const n = new Set(s); n.delete(sid!); return n })
  }, [queryClient])

  // ── Row actions ──────────────────────────────────────────────────────
  const confirm = useConfirm()

  const renameSession = useCallback(async (project: ProjectItem, id: string, title: string) => {
    const trimmed = title.trim()
    if (!trimmed) return
    try {
      await request(`/api/agent/sessions/${id}?workspaceId=${encodeURIComponent(project.id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: trimmed }),
      })
    } catch { /* best effort */ }
    void queryClient.invalidateQueries({ queryKey: ['agent', 'home-recent-sessions'] })
  }, [queryClient])

  const deleteSession = useCallback(async (project: ProjectItem, session: ChatSessionRow) => {
    const ok = await confirm({
      title: 'Delete conversation',
      message: `Delete "${session.title || 'Untitled chat'}"? This cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!ok) return
    try {
      await request(`/api/agent/sessions/${session.id}?workspaceId=${encodeURIComponent(project.id)}`, { method: 'DELETE' })
    } catch { /* best effort */ }
    setSelected(prev => prev?.session?.id === session.id ? { project: prev.project, session: { id: null, nonce: Date.now() } } : prev)
    void queryClient.invalidateQueries({ queryKey: ['agent', 'home-recent-sessions'] })
  }, [confirm, queryClient])

  /** Rail-list refresh after a turn (called by the chat's onTurnComplete).
   *  The rail is now ONE batched query, so the per-project arg is moot — kept
   *  for call-site compatibility; any turn refetches the whole rail. */
  const refreshSessionLists = useCallback((_projectId?: string) => {
    void queryClient.invalidateQueries({ queryKey: ['agent', 'home-recent-sessions'] })
  }, [queryClient])

  return {
    allProjects,
    railProjects,
    miniapps,
    shortcutProjects,
    driveAssistant: driveAssistant ?? null,
    /**
     * Unresolved, per query. False once that query has SETTLED — success or
     * error — so a caller that waits on it cannot wait on a failure for
     * ever. Still true while a fetch is PAUSED (offline): waiting is the
     * right answer there, because the data is coming when the connection
     * does, and the only cost of waiting is a query string that stays in
     * the address bar meanwhile.
     */
    projectsPending,
    driveAssistantPending,
    sessionGroups,
    busyIds,
    doneIds,
    selected,
    activeProject,
    activeSessionId,
    openSession,
    renameSession,
    deleteSession,
    refreshSessionLists,
  }
}
